import { useState, useRef, useCallback } from "react";
import { Camera, Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

interface ImageSearchUploaderProps {
  onAnalyzeComplete: (searchTerms: string[], description: string) => void;
  onCancel: () => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (analyzing: boolean) => void;
}

interface AnalyzeResult {
  searchTerms: string[];
  description: string;
  category: string;
  confidence: number;
  error?: string;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

export const ImageSearchUploader = ({
  onAnalyzeComplete,
  onCancel,
  isAnalyzing,
  setIsAnalyzing,
}: ImageSearchUploaderProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [analysisStatus, setAnalysisStatus] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const compressImage = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          
          // Calculate new dimensions (max 1024px)
          let width = img.width;
          let height = img.height;
          const maxDim = 1024;
          
          if (width > height && width > maxDim) {
            height = (height * maxDim) / width;
            width = maxDim;
          } else if (height > maxDim) {
            width = (width * maxDim) / height;
            height = maxDim;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const base64 = canvas.toDataURL("image/jpeg", 0.8);
          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const analyzeImage = useCallback(async (base64Image: string) => {
    setIsAnalyzing(true);
    setProgress(10);
    setAnalysisStatus("מעלה תמונה...");

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 80));
      }, 300);

      setProgress(30);
      setAnalysisStatus("מנתח את התכשיט...");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-jewelry-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ image: base64Image }),
        }
      );

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error("הגעת למגבלת הבקשות. נסה שוב בעוד מספר דקות.");
        }
        if (response.status === 402) {
          throw new Error("יש בעיה בשירות. נסה שוב מאוחר יותר.");
        }
        throw new Error(errorData.error || "שגיאה בניתוח התמונה");
      }

      setProgress(90);
      setAnalysisStatus("מחפש מוצרים דומים...");

      const result: AnalyzeResult = await response.json();

      if (result.error && result.confidence === 0) {
        throw new Error(result.error);
      }

      setProgress(100);
      setAnalysisStatus(`זוהה: ${result.description}`);

      // Wait a moment to show the result
      await new Promise((resolve) => setTimeout(resolve, 500));

      onAnalyzeComplete(result.searchTerms, result.description);
    } catch (error) {
      console.error("Error analyzing image:", error);
      toast({
        title: "שגיאה",
        description: error instanceof Error ? error.message : "שגיאה בניתוח התמונה",
        variant: "destructive",
      });
      setIsAnalyzing(false);
      setProgress(0);
      setAnalysisStatus("");
    }
  }, [onAnalyzeComplete, setIsAnalyzing]);

  const handleFile = useCallback(async (file: File) => {
    // Validate file type
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({
        title: "פורמט לא נתמך",
        description: "יש להעלות תמונה בפורמט JPG, PNG או WebP",
        variant: "destructive",
      });
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "קובץ גדול מדי",
        description: "גודל הקובץ המקסימלי הוא 2MB",
        variant: "destructive",
      });
      return;
    }

    try {
      const compressed = await compressImage(file);
      setPreviewUrl(compressed);
      await analyzeImage(compressed);
    } catch (error) {
      console.error("Error processing file:", error);
      toast({
        title: "שגיאה",
        description: "שגיאה בעיבוד התמונה",
        variant: "destructive",
      });
    }
  }, [compressImage, analyzeImage]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleClear = useCallback(() => {
    setPreviewUrl(null);
    setProgress(0);
    setAnalysisStatus("");
    setIsAnalyzing(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  }, [setIsAnalyzing]);

  return (
    <div className="space-y-4">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Preview and Analysis State */}
      {previewUrl ? (
        <div className="relative space-y-3">
          <div className="relative aspect-square max-w-48 mx-auto rounded-lg overflow-hidden border border-border">
            <img
              src={previewUrl}
              alt="תמונה לחיפוש"
              className="w-full h-full object-cover"
            />
            {!isAnalyzing && (
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2 h-7 w-7 bg-background/80 hover:bg-background"
                onClick={handleClear}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            {isAnalyzing && (
              <div className="absolute inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
          </div>

          {isAnalyzing && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-center text-muted-foreground">
                {analysisStatus}
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              "border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-all cursor-pointer",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base">גרור תמונה לכאן</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  או לחץ לבחירת קובץ
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                JPG, PNG, WebP • עד 2MB
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 sm:flex-none"
            >
              <Upload className="h-4 w-4 ml-2" />
              העלה תמונה
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => cameraInputRef.current?.click()}
              className="flex-1 sm:flex-none"
            >
              <Camera className="h-4 w-4 ml-2" />
              צלם תמונה
            </Button>
          </div>
        </>
      )}

      {/* Cancel button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="w-full"
        disabled={isAnalyzing}
      >
        ביטול
      </Button>
    </div>
  );
};
